/**
 * The admin looks — the one colour / font / radius source. A platform
 * admin picks one for everyone («Админы загвар»); each person keeps light /
 * dark. `classic` = the colours before the looks (exempt from the contrast
 * test, removed once A/B/C have been live). Values: the approved canvas,
 * spec `docs/superpowers/specs/2026-10-27-admin-looks-design.md`.
 * contrast: every `borderStrong` is the spec shade moved toward `fg` until
 * it reaches 3:1 on `card` (the spec values were 1.6:1).
 */
export type AdminLook = "classic" | "calm" | "fast" | "warm";
export const ADMIN_LOOKS: AdminLook[] = ["classic", "calm", "fast", "warm"];

export type LookMode = "light" | "dark";

export type LookTokens = {
  bg: string;
  card: string;
  fg: string;
  muted: string;
  border: string;
  /** Control outlines (inputs, outline buttons): ≥ 3:1 on `card`. */
  borderStrong: string;
  primary: string;
  onPrimary: string;
  warnFg: string;
  warnBg: string;
  okFg: string;
  okBg: string;
  dangerFg: string;
  dangerBg: string;
  quietBg: string;
  /** Six data colours (calendar, staff, charts), each ≥ 3:1 on `card`. */
  chart: string[];
};

export type Look = {
  id: AdminLook;
  label: string;
  font: string;
  /** Headline numbers; always rendered with tabular figures. */
  figureFont: string;
  /** Google Fonts stylesheet loaded at runtime; null = already in index.css. */
  fontHref: string | null;
  radius: number;
  radiusSm: number;
  light: LookTokens;
  dark: LookTokens;
};

export function isAdminLook(v: unknown): v is AdminLook {
  return typeof v === "string" && (ADMIN_LOOKS as string[]).includes(v);
}

const fonts = (...families: string[]) =>
  `https://fonts.googleapis.com/css2?${families.map((f) => `family=${f}`).join("&")}&display=swap`;

const LEXEND = ["Lexend", "sans-serif"].join(",");

export const LOOKS: Record<AdminLook, Look> = {
  classic: {
    id: "classic",
    label: "Сонгодог (одоогийнх)",
    font: LEXEND,
    figureFont: LEXEND,
    fontHref: null,
    radius: 10,
    radiusSm: 8,
    light: {
      bg: "#fcfcfc",
      card: "#ffffff",
      fg: "#16151c",
      muted: "#5e5d63",
      border: "#e8e6e6",
      borderStrong: "#cbcbd0",
      primary: "#0075FF",
      onPrimary: "#ffffff",
      warnFg: "#cc8400",
      warnBg: "#fff5e0",
      okFg: "#2e7c67",
      okBg: "#dbf5ee",
      dangerFg: "#db4f4a",
      dangerBg: "#f8dcdb",
      quietBg: "#f2f0f0",
      chart: ["#0075FF", "#4cceac", "#FFA500", "#db4f4a", "#A2A1A8", "#3da58a"],
    },
    dark: {
      bg: "#23222b",
      card: "#16151C",
      fg: "#e6e6e9",
      muted: "#A2A1A8",
      border: "#3f3f44",
      borderStrong: "#5e5d63",
      primary: "#0075FF",
      onPrimary: "#ffffff",
      warnFg: "#FFA500",
      warnBg: "#332100",
      okFg: "#4cceac",
      okBg: "#0f2922",
      dangerFg: "#db4f4a",
      dangerBg: "#2c100f",
      quietBg: "#27272a",
      chart: ["#0075FF", "#4cceac", "#FFA500", "#db4f4a", "#A2A1A8", "#3da58a"],
    },
  },
  calm: {
    id: "calm",
    label: "A — Тайван",
    font: ['"Golos Text"', "system-ui", "sans-serif"].join(","),
    figureFont: ['"Golos Text"', "system-ui", "sans-serif"].join(","),
    fontHref: fonts("Golos+Text:wght@400;500;600;700"),
    radius: 14,
    radiusSm: 12,
    light: {
      bg: "#F6F4EF",
      card: "#FFFFFF",
      fg: "#1D1C1A",
      muted: "#5F5B54",
      border: "#E3DED4",
      borderStrong: "#989389",
      primary: "#0E5F58",
      onPrimary: "#FFFFFF",
      warnFg: "#8A4B00",
      warnBg: "#FBEBD3",
      okFg: "#2E6B3A",
      okBg: "#E3F0E3",
      dangerFg: "#A32D22",
      dangerBg: "#F8E1DE",
      quietBg: "#EFEBE3",
      chart: ["#0E7C70", "#B5651D", "#3D6FB6", "#8E4F9E", "#A33A3A", "#5B7F2A"],
    },
    dark: {
      bg: "#161513",
      card: "#211F1C",
      fg: "#F2EFE8",
      muted: "#A9A397",
      border: "#34312C",
      borderStrong: "#6F6B64",
      primary: "#5FC2B4",
      onPrimary: "#0B1F1D",
      warnFg: "#F0B865",
      warnBg: "#3A2A12",
      okFg: "#86CC93",
      okBg: "#1E3324",
      dangerFg: "#F08A80",
      dangerBg: "#3A1A17",
      quietBg: "#2A2824",
      chart: ["#5FC2B4", "#E0A060", "#7FA8E8", "#C79AD6", "#EE8A80", "#A6C76A"],
    },
  },
  fast: {
    id: "fast",
    label: "B — Шуурхай",
    font: ['"IBM Plex Sans"', "system-ui", "sans-serif"].join(","),
    figureFont: ['"IBM Plex Mono"', "ui-monospace", "monospace"].join(","),
    fontHref: fonts("IBM+Plex+Mono:wght@500", "IBM+Plex+Sans:wght@400;500;600;700"),
    radius: 6,
    radiusSm: 6,
    light: {
      bg: "#FFFFFF",
      card: "#FFFFFF",
      fg: "#111418",
      muted: "#59616E",
      border: "#E4E6EA",
      borderStrong: "#8F939A",
      primary: "#111418",
      onPrimary: "#FFFFFF",
      warnFg: "#B8430B",
      warnBg: "#FDEDE3",
      okFg: "#1F6B3A",
      okBg: "#E4F2E8",
      dangerFg: "#B42318",
      dangerBg: "#FDE8E6",
      quietBg: "#F1F2F4",
      chart: ["#1F5FBF", "#B8430B", "#1F6B3A", "#7A3FB0", "#A0236A", "#5C6B00"],
    },
    dark: {
      bg: "#0E1013",
      card: "#171A1F",
      fg: "#EEF0F3",
      muted: "#9AA3AF",
      border: "#2A2F36",
      borderStrong: "#62676F",
      primary: "#EEF0F3",
      onPrimary: "#0E1013",
      warnFg: "#F59A62",
      warnBg: "#3A1E10",
      okFg: "#72C892",
      okBg: "#16301F",
      dangerFg: "#F28B82",
      dangerBg: "#3A1715",
      quietBg: "#22262C",
      chart: ["#6EA8FF", "#F59A62", "#72C892", "#B99AF0", "#F08AC0", "#C7D36A"],
    },
  },
  warm: {
    id: "warm",
    label: "C — Нарлаг",
    font: ["Onest", "system-ui", "sans-serif"].join(","),
    figureFont: ["Onest", "system-ui", "sans-serif"].join(","),
    fontHref: fonts("Onest:wght@400;500;600;700"),
    radius: 20,
    radiusSm: 16,
    light: {
      bg: "#FFF8EE",
      card: "#FFFFFF",
      fg: "#2A1E14",
      muted: "#6B5A4A",
      border: "#EEDFCB",
      borderStrong: "#A2917D",
      primary: "#A63D24",
      onPrimary: "#FFFFFF",
      warnFg: "#8C4A00",
      warnBg: "#FCE9CC",
      okFg: "#2D6A3E",
      okBg: "#E2F0E5",
      dangerFg: "#A3261A",
      dangerBg: "#FBE0DA",
      quietBg: "#F3EADD",
      chart: ["#A63D24", "#8C5A00", "#2D6A3E", "#2F5E9E", "#7B3F8C", "#00756B"],
    },
    dark: {
      bg: "#1A130E",
      card: "#251B14",
      fg: "#F6ECE0",
      muted: "#B8A693",
      border: "#3A2C21",
      borderStrong: "#77675A",
      primary: "#E8866B",
      onPrimary: "#1A130E",
      warnFg: "#F2B66B",
      warnBg: "#3B2814",
      okFg: "#86CC97",
      okBg: "#1F3124",
      dangerFg: "#F0907F",
      dangerBg: "#3C1B15",
      quietBg: "#2E241B",
      chart: ["#E8866B", "#F2B66B", "#86CC97", "#8FB3EE", "#CDA0DD", "#5FC2B4"],
    },
  },
};
